import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import { red } from "designSystem/styles/color";
import { disabledCursorStyling } from "designSystem/styles/style-functions";
import React, { FunctionComponent, useCallback, useState } from "react";
import FormControlLabel, { FormControlLabelProps } from "./FormControlLabel";
import Text from "../text/Text";

const DEFAULT_TEXTBOX_HEIGHT = 26;
const FIRST_LABEL_WIDTH = 100;

const labelStyles = {
	flexShrink: 0,
	marginLeft: 5,
	marginTop: 5,
	textAlign: "right",
};

const PREFIX = "FormField";

const classes = {
	errorArea: `${PREFIX}errorArea`,
	labelPlacementStartFirst: `${PREFIX}labelPlacementStartFirst`,
	labelPlacementStart: `${PREFIX}labelPlacementStart`,
};

const StyledFormControlLabel = styled(FormControlLabel)(({
	theme,
}) => ({
	[`&.${classes.errorArea}`]: {
		color: red[600],
		minHeight: 24,
		overflowWrap: "break-word",
	},
	[`&.${classes.labelPlacementStartFirst}`]: {
		"& .MuiFormControlLabel-label": {
			...labelStyles,
			width: FIRST_LABEL_WIDTH,
		},
	},
	[`&.${classes.labelPlacementStart}`]: {
		"& .MuiFormControlLabel-label": labelStyles,
	},
}));

interface ControlElementProps {
	control: JSX.Element;
	disabled?: boolean;
	fullWidth?: boolean;
	invalidMessage?: string;
	multipleInline?: boolean;
}

const ControlElement: FunctionComponent<ControlElementProps> = (props) => {
	const { control, disabled, fullWidth, invalidMessage = "", multipleInline } = props;
	const [width, setWidth] = useState(0);
	const [padding, setPadding] = useState(0);
	const invalid = !!invalidMessage ? "true" : undefined;
	const newControl = React.cloneElement(control, { disabled, invalid });

	const ref = useCallback((node: HTMLElement) => {
		if (node) {
			/* 26 is the "default" height, or the height of text boxes.  For smaller
			* inputs such as checkboxes, we want to ensure centering.
		   */
			setPadding((DEFAULT_TEXTBOX_HEIGHT - node.offsetHeight) / 2);
			if (multipleInline) {
				setWidth(node.offsetWidth);
			}
		}
	}, []);

	const boxWidth = fullWidth ? "auto" : 1;
	const boxGrow = fullWidth ? 1 : "auto";

	return (
		<Box
			flexGrow={boxGrow}
			paddingTop={padding + "px"}
			sx={{ cursor: disabledCursorStyling(disabled) }}
			width={boxWidth}
		>
			<span
				ref={ref}
				style={{
					display: "inline-block",
					width: fullWidth ? "100%" : "auto",
				}}
			>
				{newControl}
			</span>
			<Box
				className={classes.errorArea}
				width={width || "auto"}
			>
				<Text text={invalidMessage} />
			</Box>
		</Box>
	);
};

interface FormFieldProps extends FormControlLabelProps {
	/** indicate if form field value is invalid and provide message */
	invalidMessage?: string;

	/** 
	 * True if the element is not the first in its row
	 * Do not set this property, it will be filled in automatically by FormFieldRow
	 */
	notFirst?: boolean;

	/** 
	 * true if there is more than one field in the line
	 * Do not set this property, it will be filled in automatically by FormFieldRow
	 */
	multipleInline?: boolean;

	/**
	 * True if the field is not part of a normal form and needs to have its label within its box
	 */
	standalone?: boolean;
}

const FormField: FunctionComponent<FormFieldProps> = (props) => {
	const { control, disabled, fullWidth, invalidMessage, label, multipleInline, notFirst, standalone, sx, ...otherProps } = props;
	const { labelPlacementStart, labelPlacementStartFirst } = classes;
	let marginLeft = 0;
	const theme = useTheme();

	if ((notFirst || standalone) && (!props.labelPlacement || props.labelPlacement !== "start")) {
		// first label width plus total amount of margin that "start" would have
		marginLeft = FIRST_LABEL_WIDTH + 13;
	}

	return (
		<StyledFormControlLabel
			classes={{
				labelPlacementStart: (notFirst || standalone) ? labelPlacementStart : labelPlacementStartFirst,
			}}
			control={(
				<ControlElement
					control={control}
					disabled={disabled}
					fullWidth={fullWidth}
					invalidMessage={invalidMessage}
					multipleInline={multipleInline}
				/>
			)}
			disabled={disabled}
			fullWidth={fullWidth || standalone}
			label={label}
			labelStyle={{ cursor: disabledCursorStyling(disabled) }}
			standalone={standalone}
			style={{
				alignItems: "start",
				flexGrow: standalone ? 1 : "auto",
				marginLeft,
				verticalAlign: "top",
			}}
			sx={{
				"& .FormFielderrorArea" : {
					color: theme.palette.error.main,
				},
				...sx,
			}}
			{...otherProps}
		/>
	);
};

export default FormField;